import { Font } from '@react-pdf/renderer';

/**
 * @file ./models/utils/fontesDanfe.js
 *
 * Famílias tipográficas do DANFE. O MOC 7.0 (Anexo II, item 3.7) admite
 * **apenas** `Times New Roman` ou `Courier New`; qualquer outra invalida o
 * documento em conferência fiscal.
 *
 * ## Por que as fontes base-14 do PDF, e não um TTF registrado
 *
 * `Times-Roman` e `Times-Bold` são duas das catorze fontes que todo leitor
 * de PDF é obrigado a fornecer. Elas **são** o Times New Roman: mesmas
 * métricas, e o leitor substitui pela fonte real do sistema na hora de
 * exibir e imprimir. Não há arquivo para embutir, nem subsetting, nem
 * download.
 *
 * A alternativa que este arquivo usava antes — registrar o `Tinos`
 * (metricamente compatível, do Chrome OS Core Fonts) por TTF do Google
 * Fonts — **corrompe o documento**. O fontkit embarcado no
 * `@react-pdf/renderer` v1.6.17 monta o subset do Tinos com os índices de
 * glifo deslocados: o texto sai posicionado corretamente, mas com as letras
 * trocadas ou invisíveis ("CARUARU FRIOS" vira "CDRADRA URFOS"). O mesmo
 * teste com `Times-Roman` e com `Roboto` sai perfeito, o que isola o
 * problema no arquivo do Tinos e não no uso da biblioteca.
 *
 * O comentário em `PDFAdapter.jsx` afirma que as fontes built-in do pdfkit
 * falham com caracteres acentuados. Isso foi verificado e **não procede**
 * para as Times: `INSCRIÇÃO`, `OPERAÇÃO`, `SÉRIE`, `ALÍQ.`, `ELETRÔNICA`,
 * `Nº` e o conjunto `ÁÀÂÃÉÊÍÓÔÕÚÜÇ` saem corretos em Times-Roman e
 * Times-Bold, em 6 pt e em 11 pt. (O relatório de sessão continua com
 * Roboto por outros motivos; nada aqui o altera.)
 *
 * Não confundir com o gerador `pdfkit` de origem desta grade, que também
 * usa `Times-Roman` — a escolha aqui reproduz a dele.
 */

/** Corresponde a Times New Roman (MOC 3.7). */
export const FAMILIA_SERIFA = 'Times-Roman';

/** Times New Roman negrito — exigido em vários campos pelo MOC 3.7.4 a 3.7.6. */
export const FAMILIA_SERIFA_NEGRITO = 'Times-Bold';

/** Corresponde a Courier New, a outra família admitida pelo MOC. */
export const FAMILIA_MONOESPACADA = 'Courier';

let jaRegistrado = false;

/**
 * Prepara o renderer para o DANFE. As famílias em si não precisam de
 * registro — são base-14 —, então o que sobra é desligar o hifenador.
 *
 * Idempotente: pode ser chamada a cada geração de documento sem custo.
 *
 * @returns {void}
 */
export function registrarFontesDanfe() {
  if (jaRegistrado) return;

  // O hifenador padrão quebra palavras em português em pontos inválidos e,
  // pior, altera a contagem de linhas usada pelo motor de paginação.
  Font.registerHyphenationCallback((palavra) => [palavra]);

  jaRegistrado = true;
}
